// APP_STYLES — the "Construction Engineering Cockpit" design tokens for App.js.
// Warm canvas, deep navy chrome, safety-orange accents. Key names are stable so
// existing JSX keeps working. Token source of truth: ./components/theme.js + theme.css.
// Extracted verbatim from App.js (was an inline const) — pure literal, no imports.
// ──────────────────────────────
export const APP_STYLES = {
  shell: {
    minHeight: "100vh",
    width: "100vw",
    maxWidth: "100vw",
    overflowX: "hidden",
    background: "#FAFAF7",
    color: "#0E1116",
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    display: "flex",
    flexDirection: "column",
  },
  topBar: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    background: "#0E1B2C",
    backgroundImage: "linear-gradient(180deg, #15263C 0%, #0E1B2C 100%)",
    borderBottom: "1px solid #1F3550",
    boxShadow: "0 1px 0 rgba(255,255,255,0.04) inset, 0 4px 16px rgba(14, 27, 44, 0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 24px",
    height: 60,
    gap: 16,
    boxSizing: "border-box",
    width: "100%",
  },
  topBarLeft: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    flexShrink: 0,
  },
  homeButton: {
    display: "flex",
    alignItems: "center",
    gap: 11,
    background: "transparent",
    border: "1px solid transparent",
    cursor: "pointer",
    padding: "5px 10px 5px 4px",
    borderRadius: 10,
    transition: "background 0.18s cubic-bezier(0.4,0,0.2,1), border 0.18s",
    color: "#FAFAF7",
  },
  // The logo mark — a construction-tech sigil with the signature orange/navy
  homeLogo: {
    width: 36,
    height: 36,
    borderRadius: 8,
    background: "linear-gradient(135deg, #FF6B35 0%, #E55A28 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 800,
    color: "#FAFAF7",
    fontFamily: '"Geist", "Inter Tight", sans-serif',
    letterSpacing: "-0.04em",
    flexShrink: 0,
    boxShadow: "0 4px 14px rgba(255, 107, 53, 0.4), inset 0 1px 0 rgba(255,255,255,0.18)",
    position: "relative",
  },
  homeLabel: {
    fontSize: 14,
    fontWeight: 700,
    color: "#FAFAF7",
    letterSpacing: "-0.02em",
    fontFamily: '"Geist", "Inter Tight", sans-serif',
  },
  topBarDivider: {
    width: 1,
    height: 28,
    background: "rgba(255,255,255,0.08)",
    flexShrink: 0,
  },
  topBarCenter: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flex: 1,
    justifyContent: "center",
    minWidth: 0,
  },
  projectSelect: {
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.05)",
    color: "#FAFAF7",
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "-0.005em",
    outline: "none",
    cursor: "pointer",
    maxWidth: 240,
    fontFamily: '"Inter", sans-serif',
    transition: "background 0.18s, border 0.18s",
  },
  // The active section "pill" — like a flight indicator in the cockpit
  activeSectionPill: {
    background: "rgba(255, 107, 53, 0.12)",
    border: "1px solid rgba(255, 107, 53, 0.35)",
    borderRadius: 999,
    padding: "5px 14px",
    fontSize: 10,
    fontWeight: 700,
    color: "#FF6B35",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    fontFamily: '"Inter", sans-serif',
  },
  topBarRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexShrink: 0,
  },
  // The "Sections" menu trigger button
  sectionsButton: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8,
    padding: "8px 16px",
    color: "#FAFAF7",
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "-0.005em",
    cursor: "pointer",
    transition: "background 0.18s, border 0.18s",
    whiteSpace: "nowrap",
    fontFamily: '"Inter", sans-serif',
  },
  navButton: {
    width: "100%",
    textAlign: "left",
    borderRadius: 10,
    padding: "11px 14px",
    border: "1px solid transparent",
    background: "transparent",
    color: "#CBD5E1",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.18s cubic-bezier(0.4,0,0.2,1)",
  },
  navButtonActive: {
    background: "linear-gradient(135deg, #FF6B35 0%, #E55A28 100%)",
    color: "#FAFAF7",
    boxShadow: "0 6px 18px rgba(255, 107, 53, 0.35), inset 0 1px 0 rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.16)",
  },
  content: {
    padding: "24px 28px 36px",
    display: "grid",
    gap: 18,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    overflowX: "hidden",
    flex: 1,
  },
  // The big project header — now feels like a control panel:
  // warm white card, blueprint-orange-to-navy stripe across the top, generous
  // typography, with stat-style badges that look engineered.
  headerCard: {
    position: "relative",
    background: "#FFFFFF",
    borderRadius: 14,
    padding: "26px 28px",
    border: "1px solid #E5E1D8",
    boxShadow: "0 1px 2px rgba(14,17,22,0.04), 0 8px 24px rgba(14,17,22,0.06)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 20,
    flexWrap: "wrap",
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
  },
  headerTitle: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1.05,
    fontWeight: 700,
    letterSpacing: "-0.03em",
    color: "#0E1116",
    fontFamily: '"Geist", "Inter Tight", sans-serif',
  },
  headerSubtitle: {
    margin: "10px 0 0 0",
    color: "#5D6470",
    fontSize: 13,
    lineHeight: 1.55,
    maxWidth: 720,
    fontWeight: 500,
  },
  headerBadgeWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  // Badges now look like engineering data chips — compact, monospaced numbers,
  // eyebrow caps for labels. Reads as instrumentation, not as fluff.
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 8,
    background: "#F4F2EC",
    color: "#0E1116",
    fontSize: 12,
    fontWeight: 600,
    border: "1px solid #E5E1D8",
    fontVariantNumeric: "tabular-nums",
    fontFamily: '"Inter", sans-serif',
    letterSpacing: "-0.005em",
  },
  sectionCard: {
    background: "transparent",
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
    overflowX: "hidden",
  },
};
